import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai'
import { decode, decodeAudioData, createPcmBlob } from './audioUtils'
import { interviewerPrompt, geminiModelConfig, audioConfig, PromptConfig } from './prompts'
import { ConversationMemory } from './memory/ConversationMemory'
import { ConversationMemoryState } from './memory/types'
import { ChatMessage } from '@bosmart/types'
import {
  InterviewStateManager,
  StuckDetector,
  ContextInjector,
  InterviewQuestion,
  InterviewState,
} from './interview-state'

interface GeminiLiveConfig {
  apiKey: string
  prompt?: PromptConfig // Optional dynamic prompt, falls back to default
  onOpen: () => void
  onClose: () => void
  onError: (error: Error) => void
  onAudioData: (isPlaying: boolean) => void // Used to trigger visualizer
  onTranscription: (user: string, model: string) => void
  onTurnComplete: (userText: string, modelText: string) => void
  onStateUpdate?: (state: InterviewState) => void // Optional callback for state updates
}

export class GeminiLiveService {
  private ai: GoogleGenAI
  private sessionPromise: Promise<any> | null = null
  private inputAudioContext: AudioContext | null = null
  private outputAudioContext: AudioContext | null = null
  private inputSource: MediaStreamAudioSourceNode | null = null
  private processor: ScriptProcessorNode | null = null
  private outputNode: GainNode | null = null
  private outputDestination: MediaStreamAudioDestinationNode | null = null
  private nextStartTime = 0
  private sources = new Set<AudioBufferSourceNode>()
  private isPaused = false
  private isScreenSharing = false

  // State for transcriptions
  private currentInputTranscription = ''
  private currentOutputTranscription = ''

  // Interview state management (Single Source of Truth)
  private stateManager: InterviewStateManager = new InterviewStateManager()

  // Conversation memory for RAG
  private memory: ConversationMemory = new ConversationMemory()

  // Context injection (handles all context updates)
  private contextInjector: ContextInjector

  // Stuck detection (detects when AI repeats questions)
  private stuckDetector: StuckDetector = new StuckDetector()

  // Flag to prevent re-triggering initial greeting
  private hasInitialGreetingBeenSent = false

  constructor(private config: GeminiLiveConfig) {
    this.ai = new GoogleGenAI({
      apiKey: config.apiKey,
      httpOptions: { apiVersion: 'v1alpha' },
    })
    // Initialize context injector with null session (will be updated on connect)
    this.contextInjector = new ContextInjector(this.stateManager, this.memory, null)
  }

  public async connect(stream: MediaStream) {
    try {
      // Initialize Audio Contexts
      // Note: We attempt configured sample rates but accept whatever the browser/hardware gives us to avoid failure
      this.inputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: audioConfig.inputSampleRate,
      })
      this.outputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: audioConfig.outputSampleRate,
      })
    } catch (err) {
      // Clean up any partially created contexts
      if (this.inputAudioContext) {
        try {
          await this.inputAudioContext.close()
        } catch { void 0 }
        this.inputAudioContext = null
      }
      if (this.outputAudioContext) {
        try {
          await this.outputAudioContext.close()
        } catch { void 0 }
        this.outputAudioContext = null
      }
      throw new Error(
        'Failed to initialize audio contexts: ' + (err instanceof Error ? err.message : String(err))
      )
    }

    // Create output nodes for AI audio
    this.outputNode = this.outputAudioContext.createGain()

    // CRITICAL: Create destination node to capture AI audio as MediaStream
    // This allows us to record the AI's voice in addition to playing it
    this.outputDestination = this.outputAudioContext.createMediaStreamDestination()

    // Connect to both speakers AND recording destination
    this.outputNode.connect(this.outputAudioContext.destination) // For playback
    this.outputNode.connect(this.outputDestination) // For recording

    try {
      this.sessionPromise = this.ai.live.connect({
        // Update context injector with new session
        // Note: We need to wrap the session to match SessionSender interface
        model: geminiModelConfig.modelName,
        callbacks: {
          onopen: () => {
            // Update context injector with session promise for context injection
            this.contextInjector.updateSession(this.sessionPromise)
            this.config.onOpen()
            this.startAudioInput(stream)
            // Trigger AI to start speaking immediately after connection
            // Wait 1 second for connection to stabilize, then send initial greeting trigger
            setTimeout(() => {
              this.sendInitialGreeting()
            }, 1000)
          },
          onmessage: (message: LiveServerMessage) => this.handleMessage(message),
          onclose: () => this.config.onClose(),
          onerror: (_e) => this.config.onError(new Error('Session error')),
        },
        config: {
          responseModalities: [Modality.AUDIO],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: { voiceName: geminiModelConfig.voiceConfig.voiceName },
            },
          },
          systemInstruction:
            this.config.prompt?.systemInstruction || interviewerPrompt.systemInstruction,
          inputAudioTranscription: {},
          outputAudioTranscription: {},
        },
      })
    } catch (err) {
      await this.disconnect()
      throw err
    }
  }

  private startAudioInput(stream: MediaStream) {
    if (!this.inputAudioContext) return
    if (this.inputAudioContext.state === 'suspended') {
      this.inputAudioContext.resume()
    }
    if (this.processor) return // Already started

    this.inputSource = this.inputAudioContext.createMediaStreamSource(stream)

    // Use configured buffer size for optimal latency
    // Lower values = less latency but more CPU usage
    this.processor = this.inputAudioContext.createScriptProcessor(audioConfig.bufferSize, 1, 1)

    this.processor.onaudioprocess = (e) => {
      // Skip processing only if fully paused (not just screen sharing)
      if (this.isPaused) return

      const inputData = e.inputBuffer.getChannelData(0)

      // CRITICAL: Pass the actual sample rate of the context.
      // If the browser didn't give us the configured rate, we must tell Gemini the real rate
      // otherwise the audio will be pitch-shifted and unintelligible to the AI.
      const actualSampleRate = this.inputAudioContext?.sampleRate || audioConfig.inputSampleRate

      const pcmBlob = createPcmBlob(inputData, actualSampleRate)

      this.sessionPromise
        ?.then((session) => {
          if (session && typeof session.sendRealtimeInput === 'function') {
            try {
              session.sendRealtimeInput({ media: pcmBlob })
            } catch (err) { void err }
          }
        })
        .catch(() => { void 0 })
    }

    // Create a muted gain node to prevent feedback while keeping processor active
    // ScriptProcessorNode requires a connection to fire onaudioprocess events
    const muteNode = this.inputAudioContext.createGain()
    muteNode.gain.value = 0 // Mute completely - no audio playback

    this.inputSource.connect(this.processor)
    this.processor.connect(muteNode)
    muteNode.connect(this.inputAudioContext.destination)
  }

  /**
   * Send initial greeting trigger to make AI start speaking immediately
   * This sends a text trigger (preferred) or silent audio chunk to trigger the AI to respond
   * CRITICAL: Only sends once - prevents restarting interview when candidate says "Hello"
   */
  private sendInitialGreeting() {
    // CRITICAL: Prevent re-triggering if greeting already sent
    if (this.hasInitialGreetingBeenSent) {
      if (import.meta.env.DEV) { void 0 }
      return
    }

    if (!this.sessionPromise || !this.inputAudioContext) {
      return
    }

    this.hasInitialGreetingBeenSent = true // Mark as sent immediately to prevent race conditions

    this.sessionPromise
      ?.then((session) => {
        // 1. Try sending text trigger first (more reliable)
        // Use 'any' cast to access send method which might not be in the strict type definition yet
        if (session && typeof (session as any).send === 'function') {
          try {
            const send = (session as any).send as (payload: unknown) => void
            send({
              parts: [
                {
                  text: "The user has joined. Follow PHASE 1 - INTRODUCTION: Greet them warmly, introduce yourself, explain the job/position in depth using the role context provided, mention how many questions you have, then ask 'Are you ready to proceed with the interview?' and WAIT for their explicit confirmation before proceeding to ask any questions. IMPORTANT: Do NOT restart the introduction if the candidate says 'Hello' or greets you during the interview - continue with the current phase.",
                },
              ],
              turnComplete: true,
            })
            return // Success, skip audio trigger
          } catch (err) { void err }
        }

        // 2. Fallback: Send silent audio if text trigger failed or not supported
        if (session && typeof session.sendRealtimeInput === 'function') {
          try {
            // Create a minimal silent audio buffer to trigger the AI to start speaking
            // This simulates the candidate "joining" the conversation, prompting the AI to greet them
            const sampleRate = this.inputAudioContext?.sampleRate || audioConfig.inputSampleRate
            const duration = 0.2 // Increased to 200ms of silence for better detection
            const length = Math.floor(sampleRate * duration)
            const silentBuffer = new Float32Array(length)
            // Fill with silence (already zeros, but being explicit)
            silentBuffer.fill(0)

            const silentPcmBlob = createPcmBlob(silentBuffer, sampleRate)

            // Send the silent audio chunk to trigger AI response
            session.sendRealtimeInput({ media: silentPcmBlob })
          } catch (err) { void err }
        }
      })
      .catch(() => { void 0 })
  }

  public sendVideoFrame(base64Data: string) {
    // Only send if session is still active
    if (!this.sessionPromise) {
      return
    }

    this.sessionPromise
      ?.then((session) => {
        // Check if session is still valid before sending
        if (session && typeof session.sendRealtimeInput === 'function') {
          try {
            session.sendRealtimeInput({
              media: {
                mimeType: 'image/jpeg',
                data: base64Data,
              },
            })
          } catch (err) { void err }
        }
      })
      .catch(() => { void 0 })
  }

  private async handleMessage(message: LiveServerMessage) {
    // 1. Handle Transcription
    if (message.serverContent?.outputTranscription) {
      this.currentOutputTranscription += message.serverContent.outputTranscription.text
      this.config.onTranscription(this.currentInputTranscription, this.currentOutputTranscription)
    } else if (message.serverContent?.inputTranscription) {
      this.currentInputTranscription += message.serverContent.inputTranscription.text
      this.config.onTranscription(this.currentInputTranscription, this.currentOutputTranscription)
    }

    if (message.serverContent?.turnComplete) {
      // Store in memory before triggering callback
      if (this.currentInputTranscription.trim()) {
        const userMessage: ChatMessage = {
          role: 'user',
          text: this.currentInputTranscription,
          timestamp: new Date().toISOString(),
        }
        this.addMessageToMemory(userMessage)
      }

      if (this.currentOutputTranscription.trim()) {
        const modelMessage: ChatMessage = {
          role: 'model',
          text: this.currentOutputTranscription,
          timestamp: new Date().toISOString(),
        }
        this.addMessageToMemory(modelMessage)

        // Check for stuck state
        if (this.stuckDetector.checkForStuckState(this.currentOutputTranscription)) {
          this.contextInjector.injectRecoveryContext()
        }

        // Periodically inject context if needed
        if (this.contextInjector.shouldInjectPeriodicContext()) {
          this.contextInjector.injectStateUpdate('periodic-update')
        }
      }

      // Trigger turn complete with the accumulated text
      this.config.onTurnComplete(this.currentInputTranscription, this.currentOutputTranscription)

      // Reset buffers
      this.currentInputTranscription = ''
      this.currentOutputTranscription = ''
    }

    // 2. Handle Audio Output
    const base64Audio = message.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data

    // Only play audio if not screen sharing (or other suppressed states)
    if (base64Audio && this.outputAudioContext && this.outputNode) {
      this.config.onAudioData(true) // Signal visualizer

      try {
        const audioBuffer = await decodeAudioData(
          decode(base64Audio),
          this.outputAudioContext,
          audioConfig.outputSampleRate,
          1
        )

        this.nextStartTime = Math.max(this.nextStartTime, this.outputAudioContext.currentTime)

        const source = this.outputAudioContext.createBufferSource()
        source.buffer = audioBuffer
        source.connect(this.outputNode)

        source.addEventListener('ended', () => {
          this.sources.delete(source)
          if (this.sources.size === 0) {
            this.config.onAudioData(false) // Stop visualizer
          }
        })

        source.start(this.nextStartTime)
        this.nextStartTime += audioBuffer.duration
        this.sources.add(source)
      } catch (err) {
        // Failed to decode/play audio chunk, log and continue
        this.config.onAudioData(false)
      }
    }

    // 3. Handle Interruption
    if (message.serverContent?.interrupted) {
      this.stopAllAudio()
      this.nextStartTime = 0
      this.config.onAudioData(false)
    }
  }

  private stopAllAudio() {
    this.sources.forEach((source) => {
      try {
        source.stop()
      } catch { void 0 }
    })
    this.sources.clear()
  }

  /**
   * Gets the AI audio output as a MediaStream for recording
   * This captures the AI's voice that's being played back
   */
  public getAIAudioStream(): MediaStream | null {
    return this.outputDestination?.stream || null
  }

  /**
   * Set screen sharing state
   * When active: AI continues to receive input (listens) but output is suppressed (silent)
   * Also sends context updates to the AI so it knows what's happening
   *
   * CRITICAL: This method sends EXACT system messages that match the prompt expectations.
   * The AI prompt requires exact string matching for "SYSTEM_UPDATE: The user is now sharing their screen"
   * and "SYSTEM_UPDATE: Screen sharing has ended". Do not modify these strings.
   */
  public setScreenSharing(active: boolean) {
    if (this.isScreenSharing === active) return

    this.isScreenSharing = active

    // Send context update to AI with EXACT strings that match prompt expectations
    this.sessionPromise
      ?.then((session) => {
        if (session && typeof (session as any).send === 'function') {
          try {
            // CRITICAL: These messages must match EXACTLY what the prompt expects
            // Prompt looks for: "SYSTEM_UPDATE: The user is now sharing their screen"
            // and "SYSTEM_UPDATE: Screen sharing has ended"
            const text = active
              ? 'SYSTEM_UPDATE: The user is now sharing their screen'
              : 'SYSTEM_UPDATE: Screen sharing has ended'

            ;(session as any).send({
              parts: [{ text }],
              turnComplete: true,
            })
          } catch (err) { void err }
        }
      })
      .catch(() => { void 0 })

    // We NO LONGER silence the AI here. It is allowed to speak if prompted.
  }

  /**
   * Pause AI audio input and output (e.g., during screen sharing)
   */
  public pause() {
    this.isPaused = true
    this.stopAllAudio() // Stop any current AI speech
  }

  /**
   * Resume AI audio input and output (e.g., after screen sharing ends)
   */
  public resume() {
    this.isPaused = false
  }

  /**
   * Check if AI is currently paused
   */
  public getPaused(): boolean {
    return this.isPaused
  }

  public async disconnect() {
    // CRITICAL: Stop all audio immediately to prevent AI from continuing to speak
    this.isPaused = true // Prevent any new processing
    this.stopAllAudio()

    // Stop audio input processing immediately
    if (this.processor) {
      try {
        this.processor.disconnect()
      } catch (err) { void err }
      this.processor = null
    }

    if (this.inputSource) {
      try {
        this.inputSource.disconnect()
      } catch (err) { void err }
      this.inputSource = null
    }

    if (this.outputNode) {
      try {
        this.outputNode.disconnect()
      } catch (err) { void err }
      this.outputNode = null
    }

    if (this.outputDestination) {
      this.outputDestination = null
    }

    // Close audio contexts to stop all audio processing
    if (this.inputAudioContext) {
      try {
        await this.inputAudioContext.close()
      } catch (err) { void err }
      this.inputAudioContext = null
    }

    if (this.outputAudioContext) {
      try {
        await this.outputAudioContext.close()
      } catch (err) { void err }
      this.outputAudioContext = null
    }

    // Close the session connection immediately
    // This stops the AI from processing any further chunks
    try {
      const session = await Promise.race([
        this.sessionPromise,
        new Promise((_, reject) => setTimeout(() => reject(new Error('Session timeout')), 5000)),
      ])
      if (session && typeof session.close === 'function') {
        session.close()
      }
    } catch (err) { void err }

    // Clear session promise to prevent further operations
    this.sessionPromise = null
    this.contextInjector.updateSession(null) // Clear session from context injector

    // Reset greeting flag for reconnection scenarios
    this.hasInitialGreetingBeenSent = false
  }

  /**
   * Replace the input audio stream (e.g., when switching to screen share with system audio)
   */
  public replaceInput(stream: MediaStream) {
    if (this.inputSource) {
      try {
        this.inputSource.disconnect()
      } catch (err) { void err }
      this.inputSource = null
    }

    // If we have a processor, we need to disconnect it too to ensure clean state
    if (this.processor) {
      try {
        this.processor.disconnect()
      } catch (err) { void err }
      this.processor = null
    }

    this.startAudioInput(stream)
  }

  /**
   * Initialize interview state with questions
   */
  public initializeInterviewState(questions: InterviewQuestion[]): void {
    this.stateManager.initialize(questions)
    this.stuckDetector.reset()

    if (import.meta.env.DEV) { void 0 }
  }

  /**
   * Get current interview state (deep clone to prevent mutation)
   */
  public getInterviewState(): InterviewState {
    return this.stateManager.getState()
  }

  /**
   * Get conversation memory state (read-only access)
   * Returns a snapshot of memory state to prevent external mutation
   */
  public getMemory(): ConversationMemoryState {
    return this.memory.getState()
  }

  /**
   * Add message to conversation memory (internal use)
   */
  private addMessageToMemory(message: ChatMessage): void {
    this.memory.addMessage(message)
  }

  /**
   * Add question answer to memory (public method for external use)
   */
  public addQuestionAnswerToMemory(
    questionId: string,
    questionText: string,
    answerText: string
  ): void {
    this.memory.addQuestionAnswer(questionId, questionText, answerText)
  }

  /**
   * Get recent conversation summary from memory
   */
  public getRecentConversationSummary(limit: number = 5): string {
    return this.memory.getRecentSummary(limit)
  }

  /**
   * Mark current question as answered and advance to next
   */
  public markQuestionAnswered(questionId?: string): void {
    this.stateManager.markQuestionAnswered(questionId)

    if (import.meta.env.DEV) {
      const state = this.stateManager.getState()
    }

    this.notifyStateUpdate()
  }

  /**
   * Get current question
   */
  public getCurrentQuestion(): InterviewQuestion | null {
    return this.stateManager.getCurrentQuestion()
  }

  /**
   * Get next question to ask
   */
  public getNextQuestion(): InterviewQuestion | null {
    return this.stateManager.getNextQuestion()
  }

  /**
   * Inject context update into the conversation
   */
  public injectContext(contextMessage: string, reason: string = 'context-update'): void {
    this.contextInjector.injectContext(contextMessage, reason)
  }

  /**
   * Inject interview state update
   */
  public injectStateUpdate(reason: string = 'state-update'): void {
    this.contextInjector.injectStateUpdate(reason)
  }

  /**
   * Notify state update callback if provided
   */
  private notifyStateUpdate(): void {
    if (this.config.onStateUpdate) {
      this.config.onStateUpdate(this.getInterviewState())
    }
  }

  /**
   * Send a lightweight keep-alive/continue nudge to the model.
   * Enhanced version that includes state information.
   */
  public sendKeepAlive(reason: string = 'keep-alive') {
    const nextQuestion = this.getNextQuestion()
    const state = this.stateManager.getState()
    const stateInfo = nextQuestion
      ? `You are on question ${state.currentQuestionIndex + 1} of ${state.totalQuestions}. `
      : ''

    this.sessionPromise
      ?.then((session) => {
        if (session && typeof (session as any).send === 'function') {
          try {
            const send = (session as any).send as (payload: unknown) => void
            send({
              parts: [
                {
                  text: `SYSTEM_KEEPALIVE (${reason}): ${stateInfo}If you have not finished the interview, continue politely with the next single question and wait for the candidate's answer before proceeding.`,
                },
              ],
              turnComplete: true,
            })
          } catch (err) { void err }
        }
      })
      .catch(() => { void 0 })
  }

  /**
   * Enhanced keep-alive with full state context
   */
  public sendKeepAliveWithState(reason: string = 'keep-alive', _state?: InterviewState): void {
    this.injectStateUpdate(`keep-alive-${reason}`)
  }
}
